import {inject, Injectable, NgZone, OnDestroy} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { catchError, map, switchMap, take, takeUntil } from "rxjs/operators";
import { BehaviorSubject, forkJoin, Observable, of, Subject, throwError } from "rxjs";
import {
    NotificationActionsModel,
    NOTIFICATIONCONST,
    NotificationsCategory,
    RawNotification,
} from "../Models/notification.model";
import { Constants, GlobalBroadCastConstants } from "../../../models/constants";
import { MatDialog } from "@angular/material/dialog";
import { APIResponse } from "../../../models/Response";
import { SharedService } from "../../../services/shared.service";
import { RegistrationService } from "../../../services/registration.service";
import { MeetingOrganizerResponseComponent } from "../../../modules/meetings-workflow/meetings/components/meeting-organizer-response/meeting-organizer-response.component";
import { OpenTaskRequestModel, UpdateTaskDialogConstants } from "../../../modules/shared/Models/task.model";
import { UserPreferencesService } from "../../../modules/shared/Services/user-preferences.service";
import { LoginService } from "../../../services/login.service";
import { ActualURLPaths, FeaturesPaths, RootRoutePaths } from "../../../models/routes-constants";
import { CreateMeetingService } from "../../../modules/meetings-workflow/services/create-meeting.service";
import { HandleErrorService } from "../../../services/handle-error-service";
import { DashboardUtilService } from "../../dashboard/Services/dashboard-util.service";
import { DevicesService } from "../../../modules/shared/Services/devices.service";
import { Router } from "@angular/router";
import { DashboardService } from "../../dashboard/Services/dashboard.service";
import { EntityInfo } from "../../../models/custom-models/entity-info";
import { ToastrService } from "ngx-toastr";
import {SecureStorageService} from "../../../modules/core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../../../local-storage-related-data.model";
import {MeetingsService} from "../../../modules/meetings-workflow/services/meetings.service";
import {GetNotificationsRequestBody} from "../../../modules/meetings-workflow/models/meeting.model";
import {format} from "date-fns";
import {GeoFenceAPIService} from "../../../modules/geo-fence-admin/services/geo-fence-api.service";

@Injectable({
    providedIn: "root",
})
export class NotificationsService implements OnDestroy {
    serverBaseUrl = environment.apiBaseUrl;
    allNotifications: RawNotification[] = [];
    allCategories: NotificationsCategory[] = [];
    notificationsCount$ = new BehaviorSubject<number>(0);
    notificationsCountObservable = this.notificationsCount$.asObservable();
    allNotificationsBehaviorSubject = new BehaviorSubject<RawNotification[]>([]);
    allNotificationsObservable$ = this.allNotificationsBehaviorSubject.asObservable();
    notificationReceivedInBackGround$ = new BehaviorSubject<any>(null);
    notificationReceivedInBackGround = this.notificationReceivedInBackGround$.asObservable();
    allCategoriesBehaviorSubject = new BehaviorSubject<NotificationsCategory[]>([]);
    allCategoriesObservable$ = this.allCategoriesBehaviorSubject.asObservable();
    private cancelPendingRequests$: Subject<void> = new Subject();
    private notificationCategoriesSubject = new BehaviorSubject<APIResponse<NotificationsCategory[]> | null>(null);
    private notificationCategories$ = this.notificationCategoriesSubject.asObservable();
    private pendingRequests$: Subject<void> = new Subject();
    selectedEntityInfo: EntityInfo | undefined;
    private secureStorageService = inject(SecureStorageService)

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private router: Router,
        private sharedService: SharedService,
        private registrationService: RegistrationService,
        private userPreferenceService: UserPreferencesService,
        private loginService: LoginService,
        private createMeetingService: CreateMeetingService,
        private handleErrorService: HandleErrorService,
        private dashboardUtilService: DashboardUtilService,
        private deviceService: DevicesService,
        private dashboardService: DashboardService,
        private toastr: ToastrService,
        private ngZone: NgZone,
        private meetingsService: MeetingsService,
        private geoFenceAPIService: GeoFenceAPIService,
    ) {
        this.listenToNotificationClick();
        this.listenBackGroundNotification();
    }

    ngOnDestroy(): void {
        this.notificationReceivedInBackGround$.unsubscribe();
        this.cancelPendingRequests$.next();
        this.cancelPendingRequests$.complete();
        this.pendingRequests$.next();
        this.pendingRequests$.complete();
    }

    setNotificationCategories(categories: NotificationsCategory[]) {
        this.allCategories = categories;
        this.allCategoriesBehaviorSubject.next(this.allCategories);
    }

    resetNotificationCategories() {
        this.allCategories = [];
        this.allCategoriesBehaviorSubject.next(this.allCategories);
    }

    getAllNotifications(
        accountIds: number[],
        notificationId: number | null = null,
    ): Observable<APIResponse<RawNotification[]>> {
        const requestBody = {
            accountIdsList: accountIds,
            notificationId: notificationId,
        };
        const newHeaders = {
            accountIds: accountIds.toString(),
        };
        return this.http
            .post<APIResponse<RawNotification[]>>(
                `${this.serverBaseUrl}/notification/getAllNotifications`,
                requestBody,
                { headers: newHeaders },
            )
            .pipe(
                takeUntil(this.cancelPendingRequests$),
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    markAsReadNotification(notificationId: number, notification: RawNotification): Observable<any> {
        if (notification.payload) {
            const formData = new FormData();
            formData.append("accountId", notification.payload.accountId!);
            return this.http
                .post(`${this.serverBaseUrl}/notification/markNotificationRead/${notificationId}`, formData)
                .pipe(
                    catchError((error) => {
                        return throwError(error);
                    }),
                );
        } else {
            throw new Error("Notification Payload is not available");
        }
    }

    async handleNotificationClick(notification: RawNotification) {
        switch (notification.payload.notificationType) {
            case NOTIFICATIONCONST.NotificationType.UPDATE_TASK:
            case NOTIFICATIONCONST.NotificationType.CREATE_TASK:
                if (
                    notification.payload.actualType === NOTIFICATIONCONST.NotificationType.IMMEDIATE_ATTENTION &&
                    !notification.payload.taskNumber
                ) {
                    this.router.navigate([ActualURLPaths.USER_ALERTS]);
                    return;
                }
                let openTaskRequestModel = new OpenTaskRequestModel();
                openTaskRequestModel.taskNumber = notification.payload.taskNumber;
                openTaskRequestModel.teamId = notification.payload.teamId;
                if (openTaskRequestModel.taskNumber && openTaskRequestModel.teamId) {
                    const { WorkItemDialogComponent } = await import(
                        "../../../modules/task-management/update-task/work-item-dialog/work-item-dialog.component"
                    );
                    this.dialog.open(WorkItemDialogComponent, {
                        disableClose: true,
                        minWidth: UpdateTaskDialogConstants.minWidth,
                        maxWidth: UpdateTaskDialogConstants.maxWidth,
                        height: UpdateTaskDialogConstants.height,
                        panelClass: UpdateTaskDialogConstants.panelClass,
                        data: { openTaskRequestModel: openTaskRequestModel, scrollTo: notification.payload.scrollTo },
                    });
                }
                break;
            case NOTIFICATIONCONST.NotificationType.TIMESHEET_REMINDER:
                this.router.navigate([FeaturesPaths.MYTIMESHEET]);
                break;
            case NOTIFICATIONCONST.NotificationType.VIEW_MEETING:
                this.openMeetingView(notification);
                break;
            case NOTIFICATIONCONST.NotificationType.EDIT_MEETING:
                this.openMeetingView(notification);
                break;
            case NOTIFICATIONCONST.NotificationType.IMMEDIATE_ATTENTION:
                this.router.navigate([FeaturesPaths.IMMEDIATE_ATTENTION]);
                break;
            case NOTIFICATIONCONST.NotificationType.MEETING_ORGANIZER_RESPONSE:
                this.handleMeetingOrganizerResponseNotificationClick(notification);
                break;
            case NOTIFICATIONCONST.NotificationType.GROUP_MESSAGE:
                this.router.navigate([FeaturesPaths.GROUP_CONVERSATIONS]);
                break;
            case NOTIFICATIONCONST.NotificationType.USER_REMINDER:
                this.router.navigate([FeaturesPaths.DASHBOARD]);
                break;
            case NOTIFICATIONCONST.NotificationType.TEMPLATE_UPDATE:
                this.router.navigate([RootRoutePaths.TEMPLATES]);
                break;
            case NOTIFICATIONCONST.NotificationType.UPDATED_USER_NAME:
                console.log("Updated User Name Notification Clicked", notification);
                this.openUserProfileDialog();
                break;
            case NOTIFICATIONCONST.NotificationType.CHAT_MESSAGE_ALERTS:
                console.log("Chat Message Alerts Notification Clicked", notification);
                this.redirectOrOpenChat(notification);
                break;
            case NOTIFICATIONCONST.NotificationType.LEAVE_NOTIFICATION:
                this.router.navigate([RootRoutePaths.LEAVE_WORKFLOW]);
                break;
            case NOTIFICATIONCONST.NotificationType.IMPORT_JIRA:
                this.router.navigate([RootRoutePaths.TASKMASTER]);
                break;
            case NOTIFICATIONCONST.NotificationType.STICKY_NOTES_NOTIFICATION:
                this.router.navigate([RootRoutePaths.STICKY_NOTES], {
                    queryParams: { noteId: notification.payload.stickyNoteId }
                });
                break;
            case NOTIFICATIONCONST.NotificationType.PUNCH_REQUEST:
                this.handlePunchInRequestNotificationClick(notification);
                break;
            default:
                console.log("No Notification Click Action Found");
                break;
        }
    }

    /**
     * Navigates to a route with query parameters.
     * If the user is already on the target route, it only updates the query params.
     * @param notification The notification object containing the payload.
     */
    redirectOrOpenChat(notification: RawNotification): void {
        const { entityId, entityTypeId } = notification.payload;

        if (!entityId || !entityTypeId) {
            console.error("Entity ID or Entity Type ID is missing in the notification payload.");
            return;
        }

        // 2. Define the target path and query parameters
        const targetPath = `/${FeaturesPaths.GROUP_CONVERSATIONS}`;
        const queryParams = { entityId, entityTypeId };

        this.sharedService.showSpinner();
        this.router
            .navigate([targetPath], {
                queryParams,
            })
            .then(() => {
                this.sharedService.hideSpinner();
            });
    }

    async openUserProfileDialog() {
        const { UserProfileComponent } = await import(
            "../../../modules/user/user-profile/user-profile.component"
        );
        this.dialog.open(UserProfileComponent, {
            width: this.deviceService.isMobile() ? "90vw" : "50vw",
            panelClass: "user-preference-dialog-container",
        });
    }

    private handlePunchInRequestNotificationClick(notification: RawNotification): void {
        const orgId = notification.payload.orgId;
        const accountId = this.sharedService.getAccountHeaderIdByOrg(String(orgId));

        if (!orgId || !accountId || !notification.payload.punchRequestId) return;

        const requestedDialogImport = import(
            "../../../modules/geo-fence-attendance/components/punch-in-requested-dialog/punch-in-requested-dialog.component"
            );

        this.geoFenceAPIService.getPunchRequestById(orgId, notification.payload.punchRequestId, accountId).subscribe({
            next: async (response) => {
                const punchRequest = response.data;
                if (punchRequest.activeNow && punchRequest.secondsRemaining > 0) {
                    const { PunchInRequestedDialogComponent } = await requestedDialogImport;
                    this.dialog.open(PunchInRequestedDialogComponent, {
                        disableClose: false,
                        autoFocus: false,
                        width: "400px",
                        data: {
                            punchRequestId: punchRequest.id,
                            expiresAt: punchRequest.expiresAt,
                        },
                    });
                } else {
                    this.toastr.error("This Punch-in request has expired. Please ask your admin to send a new request.");
                }
            },
            error: () => {
                this.toastr.error("Unable to load punch request details.");
            },
        });
    }

    openMeetingView(notification: RawNotification) {
        const meetingId = notification.payload.meetingId;
        const teamId = notification.payload.teamId;
        if (!meetingId || !teamId) {
            return;
        }
        const accountIds = this.sharedService.getAllAccountIdsOfLoggedInUser() as string;

        const requestBody = new GetNotificationsRequestBody();
        requestBody.toDate = format(
            new Date(new Date().setDate(new Date().getDate() + 7)),
            "yyyy-MM-dd'T'23:59:59"
        );
        requestBody.fromDate = format(
            new Date(new Date().setDate(new Date().getDate() - 7)),
            "yyyy-MM-dd'T'00:00:00"
        );

        this.sharedService.showSpinner();

        this.meetingsService
            .getAllExpendedViewMeetings(requestBody)
            .pipe(
                switchMap(expandedMeetingsResponse => {
                    // Find the meeting in the expanded view response (has attendeeRequestList)
                    const meetingFromExpandedView = expandedMeetingsResponse.data?.meetingResponses?.find(
                        (m: any) => m.meetingId === Number(meetingId)
                    );

                    return this.createMeetingService.openMeetingView(
                        Number(meetingId),
                        accountIds
                    ).pipe(
                        map(result => ({
                            ...result,
                            // Use meeting from expanded view (has attendeeRequestList) if found,
                            // otherwise fall back to result.meeting
                            meeting: meetingFromExpandedView || result.meeting
                        }))
                    );
                })
            )
            .subscribe({
                next: (result) => {
                    this.sharedService.hideSpinner();
                    this.dashboardUtilService.emitUsersToAssignMeetingEvent(result.teamMembers);
                    this.dashboardUtilService.emitSelectedMeetingCardEvent(result.meeting);
                },
                error: (error) => {
                    this.sharedService.hideSpinner();
                    this.handleErrorService.handleError(error);
                }
            });
    }

    handleMeetingOrganizerResponseNotificationClick(notification: RawNotification) {
        this.dialog.open(MeetingOrganizerResponseComponent, {
            maxWidth: "89vw",
            maxHeight: "90vh",
            panelClass: "meeting-organizer-response-dialog-container",
            data: { meetingPayload: notification.payload },
        });
    }

    deleteSelectedNotifications(notificationsActions: NotificationActionsModel): Observable<any> {
        return this.http.post(`${this.serverBaseUrl}/notification/clearNotification`, notificationsActions).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    markSelectedNotificationsAsRead(notificationsActions: NotificationActionsModel): Observable<any> {
        return this.http.post(`${this.serverBaseUrl}/notification/markNotificationRead`, notificationsActions).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    markAllAsRead(): Observable<any> {
        return this.http.post(`${this.serverBaseUrl}/notification/markAllNotificationRead`, null).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    getNotificationsData(notificationId: number | null = null) {
        const accountIds = this.sharedService.getAllAccountIdsOfLoggedInUser(false) as number[];
        this.pendingRequests$.next();
        if (accountIds.length > 0) {
            let getNotificationCategories$ = this.getNotificationCategories(accountIds.toString());
            let getAllNotifications$ = this.getAllNotifications(accountIds, notificationId);

            forkJoin([getNotificationCategories$, getAllNotifications$])
                .pipe(takeUntil(this.pendingRequests$))
                .subscribe({
                    next: ([categories, notifications]) => {
                        let allCategories = categories.data?.sort((a, b) => {
                            return a.priority - b.priority;
                        });
                        this.setNotificationCategories(allCategories);
                        const notificationsResponse = notifications.data;
                        if (notificationsResponse.length > 0) {
                            if (notificationId) {
                                this.allNotifications = [...notificationsResponse, ...this.allNotifications];
                            } else {
                                this.allNotifications = notificationsResponse;
                            }
                            this.allNotifications.sort((a, b) => {
                                return Number(b.notificationId) - Number(a.notificationId);
                            });
                            this.allNotificationsBehaviorSubject.next(this.allNotifications);
                            this.updateNotificationCount();
                        }
                    },
                    error: (error) => {
                        console.log(error);
                    },
                });
        } else {
            console.log("No Account Ids Found");
        }
    }

    getNotificationsCount() {
        let length = this.allNotifications.filter(
            (notification: RawNotification) => notification.isRead === "false",
        ).length;
        if (length > 0) {
            return length;
        } else {
            return 0;
        }
    }

    updateNotificationCount() {
        this.notificationsCount$.next(this.getNotificationsCount());
    }

    setAllNotificationNewValue(notifications: RawNotification[]) {
        this.allNotifications = notifications;
        this.allNotificationsBehaviorSubject.next(this.allNotifications);
        this.updateNotificationCount();
    }

    pushNewNotificationToAllNotifications(notification: RawNotification) {
        this.allNotifications.unshift(notification);
        this.allNotificationsBehaviorSubject.next(this.allNotifications);
        this.updateNotificationCount();
    }

    resetNotificationsData() {
        this.allNotifications = [];
        this.allNotificationsBehaviorSubject.next(this.allNotifications);
        this.updateNotificationCount();
    }

    getNotificationCategories(accountIds: string) {
        return this.notificationCategories$.pipe(
            take(1),
            switchMap((cachedData) => {
                if (cachedData) {
                    return of(cachedData);
                } else {
                    let newHeaders = { accountIds: accountIds };
                    return this.http
                        .get<APIResponse<NotificationsCategory[]>>(
                            `${this.serverBaseUrl}/notification/category/getAllCategories`,
                            {
                                headers: newHeaders,
                            },
                        )
                        .pipe(
                            catchError((error) => {
                                return throwError(error);
                            }),
                            switchMap((data) => {
                                this.notificationCategoriesSubject.next(data);
                                return of(data);
                            }),
                        );
                }
            }),
        );
    }

    getRecentNotificationId() {
        if (this.allNotifications.length > 0) {
            return Number(this.allNotifications[0].notificationId);
        } else {
            return null;
        }
    }

    private shouldRefreshPage(notificationPayload: any): boolean {
        if (!this.selectedEntityInfo) {
            return false;
        }

        const {
            entityId: notifEntityId,
            entityTypeId: notifEntityTypeId,
            projectId: notifProjectId,
            teamId: notifTeamId,
            orgId: notifOrgId
        } = notificationPayload;

        const { entityId, entityTypeId } = this.selectedEntityInfo;

        if (notifEntityId && notifEntityTypeId &&
            entityId === notifEntityId &&
            entityTypeId === notifEntityTypeId) {
            return true;
        }

        const entityTypeMatches: Record<number, number | undefined> = {
            [Constants.EntityTypes.PROJECT]: notifProjectId,
            [Constants.EntityTypes.TEAM]: notifTeamId,
            [Constants.EntityTypes.ORG]: notifOrgId
        };

        return entityTypeMatches[entityTypeId] === entityId;
    }

    private listenToNotificationClick(): void {
        const pushNotificationClickChannel = new BroadcastChannel(GlobalBroadCastConstants.PUSH_NOTIFICATION_CLICK);
        pushNotificationClickChannel.onmessage = (event) => {
            this.secureStorageService.setItem(LocalStorageRelatedData.NOTIFICATION_DATA , event.data);
        };
    }

    private listenBackGroundNotification(): void {
        const pushNotificationClickChannel = new BroadcastChannel("push-notification-generated");
        pushNotificationClickChannel.onmessage = (event) => {


            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.NEW_ACCESS_DOMAIN) {
                const userName = this.sharedService.getLoggedInUserName();
                if (userName) {
                    this.registrationService.fetchAndUpdateUserDetails(userName).subscribe({});
                }
            }

            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.UPDATED_ACCESS_DOMAIN) {
                const userName = this.sharedService.getLoggedInUserName();
                if (userName) {
                    this.registrationService.fetchAndUpdateUserDetails(userName).subscribe({
                        next: () => {
                            // Check if the notification matches current user's project/team
                            if (this.shouldRefreshPage(event.data.data)) {
                                setTimeout(() => {
                                    window.location.reload();
                                }, 500);
                            }
                        }
                    });
                }
            }
            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.MEETING_ANALYSIS) {
                this.handleMeetingAnalysisComplete(event.data.data);
            }
            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.IMMEDIATE_ATTENTION) {
                this.sharedService.setIsUserReceivedImmediateAttention(true);
            }
            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.USER_PREFERENCE_UPDATE) {
                this.userPreferenceService.getAndSetUserPreferences();
                return;
            }

            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.USER_REMOVE_UPDATE) {
                //     logout the user and return
                this.toastr.warning(Constants.ErrorMessages.ACCESS_CHANGED_MESSAGE);

                // Delay logout by 2.5 seconds to show the message
                setTimeout(() => {
                    this.ngZone.run(() => {
                        this.loginService.logoutTheUserAndRedirectToLogin();
                    });
                }, 2500);
                return;
            }
            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.STICKY_NOTES_NOTIFICATION) {
                this.toastr.info(event.data.data.body, event.data.data.title, { timeOut: 5000 });
            }

            if (event.data.data.notificationType === NOTIFICATIONCONST.NotificationType.USER_REMINDER) {
                this.toastr.info(event.data.data.body, event.data.data.title, { timeOut: 5000 });
            }

            this.notificationReceivedInBackGround$.next(event.data.data);
        };
    }

    /**
     * Handles meeting analysis completion notification
     * Fetches updated meeting data and updates the expanded view
     */
    private handleMeetingAnalysisComplete(notificationPayload: any): void {
        const meetingId = notificationPayload.meetingId;
        const accountId = notificationPayload.accountId;
        if (!meetingId || !accountId) return;

        this.createMeetingService.getMeetingByMeetingId(Number(meetingId), String(accountId))
            .subscribe({
                next: (response) => {
                    if (response.data) {
                        // Update the meeting in expanded view using dashboardUtilService
                        this.meetingsService.updateExpandedViewMeeting(response.data);
                    }
                },
                error: (error) => {
                    console.error('Error fetching updated meeting after analysis:', error);
                    this.handleErrorService.handleError(error);
                }
            });
    }
}
